import { useState } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import type { TimeEntryWarningCode } from '@ast-data/shared';

interface TimeEntryFlagProps {
    /** The row is double-booked (same worker, overlapping times that day). */
    conflict: boolean;
    /** ArbZG warning codes for the row (short break / short rest). */
    codes: TimeEntryWarningCode[];
    /**
     * Unfold the row's detail panel instead of toggling the tooltip — the
     * office grid, where the panel says the same thing at length AND links to
     * the other booking. Undefined where there is no panel (the field grids).
     */
    onToggle?: () => void;
    /** Whether that panel is currently open — for `aria-expanded`. */
    expanded?: boolean;
}

/**
 * The leading status button for a time-entry row: a red error for a
 * double-booking, else an amber warning for the ArbZG break/rest issues.
 *
 * What a tap does depends on whether the row has somewhere to unfold TO. With
 * `onToggle` it opens the detail panel; without one it toggles a tooltip that
 * names the reasons, click-driven so it works on touch.
 *
 * Renders nothing when the row is clean; shared by both grids.
 */
export function TimeEntryFlag({
    conflict,
    codes,
    onToggle,
    expanded
}: TimeEntryFlagProps) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    if (!conflict && codes.length === 0) {
        return null;
    }
    const lines: string[] = [];
    if (conflict) {
        lines.push(intl.formatMessage({ id: 'timeEntry.conflict' }));
    }
    for (const code of codes) {
        lines.push(intl.formatMessage({ id: `timeEntry.warning.${code}` }));
    }
    return (
        <Tooltip
            title={
                <Box>
                    {lines.map((line, index) => (
                        <Box key={index}>{line}</Box>
                    ))}
                </Box>
            }
            // With a panel behind it the tooltip is a hover preview of what
            // unfolding shows; without one it IS the answer, and a tap has to
            // open it.
            {...(onToggle ? {} : { open, onClose: () => setOpen(false) })}
        >
            <IconButton
                size="small"
                aria-label={lines.join('. ')}
                aria-expanded={onToggle ? !!expanded : undefined}
                onClick={(event) => {
                    // The row's own click handlers must not fire — a click in
                    // the cell would otherwise also start an inline edit.
                    event.stopPropagation();
                    if (onToggle) {
                        onToggle();
                        return;
                    }
                    setOpen((prev) => !prev);
                }}
            >
                {conflict ? (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                ) : (
                    <WarningAmberIcon color="warning" fontSize="small" />
                )}
            </IconButton>
        </Tooltip>
    );
}
